import { useEffect, useRef, useState } from 'react'
import { message } from 'antd'

import useInputUpload from '@/hooks/useInputUpload'

const handleDragOver = (e: DragEvent) => {
  if (!e.dataTransfer?.items || e.dataTransfer.items.length === 0) return

  const isFile = e.dataTransfer.types.includes('Files')
  if (isFile) {
    e.preventDefault()
  }
}

const processEntry = async (entry: FileSystemEntry): Promise<File[]> => {
  return await new Promise(resolve => {
    if (entry.isFile) {
      ;(entry as FileSystemFileEntry).file(file => {
        resolve([file])
      })
    } else if (entry.isDirectory) {
      const dirReader = (entry as FileSystemDirectoryEntry).createReader()
      dirReader.readEntries(async entries => {
        const filesPromises = entries.map(element => processEntry(element))
        const fileArrays = await Promise.all(filesPromises)
        resolve(fileArrays.flat())
      })
    } else {
      resolve([])
    }
  })
}

const getFileListFromDataTransferItems = async (items: DataTransferItem[]) => {
  // get filesList
  const filePromises: Array<Promise<File[]>> = []
  for (const item of items) {
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry()
      // if (entry) {
      //   filePromises.push(processEntry(entry))
      // } else {
      const file = item.getAsFile()

      if (file) {
        filePromises.push(
          new Promise(resolve => {
            resolve([file])
          }),
        )
      }
      // }
    }
  }

  const fileArrays = await Promise.all(filePromises)
  return fileArrays.flat()
}

const useDragUpload = () => {
  const [isDragging, setIsDragging] = useState(false)
  const { beforeUpload } = useInputUpload()

  // When a file is dragged to a different area, the 'dragleave' event may be triggered,
  // causing isDragging to be mistakenly set to false.
  // to fix this issue, use a counter to ensure the status change only when drag event left the browser window .
  const dragCounter = useRef(0)

  const handleDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer?.items || e.dataTransfer.items.length === 0) return

    const isFile = e.dataTransfer.types.includes('Files')
    if (isFile) {
      dragCounter.current += 1
      e.preventDefault()
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: DragEvent) => {
    if (!e.dataTransfer?.items || e.dataTransfer.items.length === 0) return

    const isFile = e.dataTransfer.types.includes('Files')
    if (isFile) {
      e.preventDefault()

      // reset counter
      dragCounter.current -= 1

      if (dragCounter.current === 0) {
        setIsDragging(false)
      }
    }
  }

  const handleDrop = async (e: DragEvent) => {
    if (!e.dataTransfer?.items || e.dataTransfer.items.length === 0) return

    const isFile = e.dataTransfer.types.includes('Files')
    if (!isFile) return

    e.preventDefault()

    // reset counter
    dragCounter.current = 0

    setIsDragging(false)
    const items = Array.from(e.dataTransfer?.items)

    const files = await getFileListFromDataTransferItems(items)

    if (files.length === 0) return

    // upload files
    onUploadFiles(files)
  }

  const handlePaste = async (
    event: React.ClipboardEvent<HTMLTextAreaElement>,
  ) => {
    // event.preventDefault()
    // get files from clipboard
    if (!event.clipboardData) return
    const items = Array.from(event.clipboardData?.items)
    // if (items.find(item => item.kind === 'file')) {
    //   // 阻止粘贴文字
    //   event.preventDefault()
    // }
    const files = await getFileListFromDataTransferItems(items)
    if (files.length === 0) return

    onUploadFiles(files)
  }

  const onUploadFiles = (fileList: File[]) => {
    if (!fileList || fileList.length === 0) return
    // 目前只上传第一个文件
    // beforeUpload(fileList[0])
    if (
      fileList.length > 1 &&
      fileList.find((file: any) => !file.type?.match('image.*'))
    ) {
      message.error('文件暂不支持多个上传')
    } else {
      fileList.forEach(async (file: any) => {
        await beforeUpload(file)
      })
    }
  }

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', handleDrop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop])

  return { isDragging, handlePaste }
}

export default useDragUpload
